import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { UpgradeModal, type UpgradeRequest } from '@/shared/access/upgrade-modal';

interface UpgradeContextValue {
    /** Open the shared Upgrade popup. Called by guards when access is denied. */
    requestUpgrade: (request?: UpgradeRequest) => void;
    /** Close it imperatively (rarely needed — the modal closes itself). */
    closeUpgrade:   () => void;
}

const UpgradeContext = createContext<UpgradeContextValue | null>(null);

/**
 * Mounts the single, app-wide Upgrade popup and exposes `requestUpgrade`. Place
 * once inside the dashboard chrome so every page (and the sidebar) can open the
 * same modal instead of each owning its own paywall UI.
 */
export function UpgradeProvider({ children }: { children: ReactNode }) {
    const [request, setRequest] = useState<UpgradeRequest | null>(null);

    const requestUpgrade = useCallback((req?: UpgradeRequest) => setRequest(req ?? {}), []);
    const closeUpgrade = useCallback(() => setRequest(null), []);

    const value = useMemo<UpgradeContextValue>(
        () => ({ requestUpgrade, closeUpgrade }),
        [requestUpgrade, closeUpgrade],
    );

    return (
        <UpgradeContext.Provider value={value}>
            {children}
            {request && <UpgradeModal request={request} onClose={closeUpgrade} />}
        </UpgradeContext.Provider>
    );
}

/** Access the Upgrade popup. Throws if used outside `UpgradeProvider`. */
export function useUpgrade(): UpgradeContextValue {
    const ctx = useContext(UpgradeContext);
    if (!ctx) throw new Error('useUpgrade must be used within an UpgradeProvider');
    return ctx;
}
